import React, { useId } from "react";
import { FormHelperText, Switch } from "@mui/material";
import { styled } from "@mui/system";

// Compact settings row: a small switch with its label (and an optional grey hint) right
// beside it, a hairline between rows. Built for long lists of on/off settings — the Outlet
// Print Settings tab — where InputSwitch's 42x24 switch, 8px margin and 18px label made
// every row a different width and the page twice as tall as it needed to be.
//
// Same props and the same onChange(event) as InputSwitch, so `handleInpCheck` from
// UseForm works unchanged and a swap is mechanical. InputSwitch is left in place for the
// screens that have not moved over yet.
//
// With no `label` it renders the bare switch (pass `ariaLabel`), for a card that already
// carries its own title — see Setup/Outlet/CustomTemplateCard.

const SmallSwitch = styled(Switch)(({ theme }) => ({
  width: 32,
  height: 18,
  padding: 0,
  display: "flex",
  flexShrink: 0,
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(14px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "var(--primary-color)",
      },
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.45,
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      outline: "2px solid var(--primary-color)",
      outlineOffset: 2,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 1px 2px 0 rgb(0 35 11 / 25%)",
    width: 14,
    height: 14,
    borderRadius: 7,
  },
  "& .MuiSwitch-track": {
    borderRadius: 9,
    opacity: 1,
    backgroundColor:
      theme.palette?.mode === "dark" ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.22)",
    boxSizing: "border-box",
  },
}));

const InputToggle = ({
  label,
  hint,
  name,
  checked,
  onChange,
  disabled = false,
  error = null,
  ariaLabel,
  className = "",
}) => {
  const hintId = useId();

  const control = (
    <SmallSwitch
      name={name}
      checked={!!checked}
      onChange={onChange}
      disabled={disabled}
      inputProps={{
        "aria-label": label ? undefined : ariaLabel,
        "aria-describedby": hint ? hintId : undefined,
      }}
    />
  );

  if (!label) return control;

  return (
    <div className={`min-w-0 border-b border-slate-100 last:border-b-0 ${className}`}>
      {/* Switch leads, label sits right beside it: a switch pushed to the far edge of a
          wide row read as detached from its label. Centred on the whole label + hint
          block, so a row with a hint reads as one unit. */}
      <label
        className={`flex items-center gap-[10px] py-[9px] select-none ${
          disabled ? "cursor-default opacity-60" : "cursor-pointer"
        }`}
      >
        <span className="flex shrink-0">{control}</span>
        <span className="min-w-0">
          <span className="block text-[13px] leading-[20px] text-gray-700">{label}</span>
          {hint && (
            <span id={hintId} className="block text-[11px] leading-snug text-gray-400">
              {hint}
            </span>
          )}
        </span>
      </label>
      {error && <FormHelperText className="color-danger !mt-0 !mb-1">{error}</FormHelperText>}
    </div>
  );
};

export default InputToggle;
